import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChartBarIcon,
  TrophyIcon,
  NewspaperIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import RegistrationModal from '../components/RegistrationModal';
import PartnerForm from '../components/PartnerForm';
import EventModal from '../components/EventModal';
import { getPlayersByTournament, getPartnersByTournament, getTournamentEvents } from '../lib/supabase';

interface ManagerDashboardHomeProps {
  tournamentId: string;
}

interface ManagerStats {
  players: { total: number; registered: number; pending: number; international: number };
  partners: { total: number; value: number; active: number; pending: number };
  events: { total: number; upcoming: number; completed: number };
  pressRegistrations: number;
  registrationRevenue: number;
}

const defaultStats: ManagerStats = {
  players: { total: 0, registered: 0, pending: 0, international: 0 },
  partners: { total: 0, value: 0, active: 0, pending: 0 },
  events: { total: 0, upcoming: 0, completed: 0 },
  pressRegistrations: 0,
  registrationRevenue: 0,
};

function ManagerDashboardHome({ tournamentId }: ManagerDashboardHomeProps) {
  const [stats, setStats] = useState<ManagerStats>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  useEffect(() => {
    if (tournamentId) fetchDashboardData();
  }, [tournamentId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [playersRes, partnersRes, eventsRes, pressRes, tournamentRes] = await Promise.all([
        getPlayersByTournament(tournamentId),
        getPartnersByTournament(tournamentId),
        getTournamentEvents(tournamentId),
        supabase.from('press_registrations').select('id', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
        supabase.from('tournaments').select('registration_fee').eq('id', tournamentId).maybeSingle(),
      ]);

      const playersData = playersRes.data || [];
      const partnersData = partnersRes.data || [];
      const eventsData = eventsRes.data || [];
      const now = new Date();

      const registrationFee = tournamentRes.data?.registration_fee || 50000;
      const { data: allPlayers } = await supabase
        .from('players')
        .select('id, registration_status')
        .eq('tournament_id', tournamentId);
      const confirmedPlayers = allPlayers?.filter(p => {
        const rs = p.registration_status as { payment?: boolean } | null;
        return rs?.payment === true;
      }).length || 0;

      setStats({
        players: {
          total: playersData.length,
          registered: playersData.filter(p => p.status?.toLowerCase() === 'active').length,
          pending: playersData.filter(p => p.status?.toLowerCase() === 'pending').length,
          international: playersData.filter(p => p.country && p.country.toLowerCase() !== 'nigeria').length,
        },
        partners: {
          total: partnersData.length,
          active: partnersData.filter(p => p.status === 'active').length,
          pending: partnersData.filter(p => p.status === 'pending').length,
          value: partnersData.reduce((sum, p) => sum + (p.contribution || 0), 0),
        },
        events: {
          total: eventsData.length,
          upcoming: eventsData.filter(e => new Date(e.date) > now).length,
          completed: eventsData.filter(e => new Date(e.date) <= now).length,
        },
        pressRegistrations: pressRes.count || 0,
        registrationRevenue: registrationFee * confirmedPlayers,
      });
    } catch (err) {
      console.error('Error fetching manager dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Players',
      value: stats.players.total,
      icon: UserGroupIcon,
      color: 'text-blue-600',
      bg: 'bg-blue-500/10',
      detail: `${stats.players.registered} active, ${stats.players.pending} pending`,
    },
    {
      label: 'Partners',
      value: stats.partners.total,
      icon: BuildingOfficeIcon,
      color: 'text-teal-600',
      bg: 'bg-teal-500/10',
      detail: stats.partners.value > 0 ? `\u20A6${(stats.partners.value / 1000000).toFixed(1)}M value` : `${stats.partners.active} active`,
    },
    {
      label: 'Upcoming Events',
      value: stats.events.upcoming,
      icon: CalendarIcon,
      color: 'text-orange-600',
      bg: 'bg-orange-500/10',
      detail: `${stats.events.total} total, ${stats.events.completed} completed`,
    },
    {
      label: 'Registration Revenue',
      value: stats.registrationRevenue,
      icon: BanknotesIcon,
      color: 'text-green-600',
      bg: 'bg-green-500/10',
      isCurrency: true,
      detail: `${stats.players.registered} confirmed players`,
    },
    {
      label: 'Press Registrations',
      value: stats.pressRegistrations,
      icon: NewspaperIcon,
      color: 'text-rose-600',
      bg: 'bg-rose-500/10',
      detail: 'Media accreditations',
    },
    {
      label: 'International Players',
      value: stats.players.international,
      icon: TrophyIcon,
      color: 'text-sky-600',
      bg: 'bg-sky-500/10',
      detail: `of ${stats.players.total} total`,
    },
  ];

  const quickActions = [
    { label: 'Add Player', description: 'Register a new player', icon: UserGroupIcon, action: () => setIsPlayerModalOpen(true) },
    { label: 'Add Partner', description: 'Create new partnership', icon: BuildingOfficeIcon, action: () => setIsPartnerModalOpen(true) },
    { label: 'Schedule Event', description: 'Plan tournament events', icon: CalendarIcon, action: () => setIsEventModalOpen(true) },
  ];

  const navLinks = [
    { label: 'Scores', tab: 'scores', icon: ChartBarIcon },
    { label: 'Finance', tab: 'finance', icon: BanknotesIcon },
    { label: 'Analytics', tab: 'analytics', icon: ChartBarIcon },
    { label: 'Messaging', tab: 'messaging', icon: ChatBubbleLeftRightIcon },
    { label: 'Press', tab: 'press', icon: NewspaperIcon },
    { label: 'Tournaments', tab: 'tournaments', icon: TrophyIcon },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-teal-500/10 p-2 rounded-lg">
          <ChartBarIcon className="w-5 h-5 text-teal-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-golf-gray">Manager Dashboard</h2>
          <p className="text-sm text-gray-500">Tournament operations overview</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.07 }}
            className="bg-white rounded-xl shadow-sm p-5 border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className={`${card.bg} p-2.5 rounded-xl`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{card.label}</p>
                <div className="text-xl font-bold text-golf-gray">
                  {card.isCurrency ? (
                    card.value > 0
                      ? new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(card.value)
                      : '\u20A60'
                  ) : (
                    <CountUp end={card.value} duration={1.5} />
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">{card.detail}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickActions.map((action, i) => (
          <motion.button
            key={action.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
            className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 hover:border-golf-orange transition-colors text-left"
            onClick={action.action}
          >
            <div className="flex items-center gap-3">
              <div className="bg-golf-orange/10 p-2.5 rounded-xl">
                <action.icon className="w-5 h-5 text-golf-orange" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">{action.label}</p>
                <p className="text-xs text-gray-500">{action.description}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-golf-gray">Quick Navigation</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {navLinks.map(link => (
            <button
              key={link.tab}
              onClick={() => {
                const event = new CustomEvent('navigate-tab', { detail: link.tab });
                window.dispatchEvent(event);
              }}
              className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 transition-colors border-r border-b border-gray-100 last:border-r-0"
            >
              <div className="bg-gray-100 p-2 rounded-lg">
                <link.icon className="w-4 h-4 text-gray-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">{link.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      <RegistrationModal
        isOpen={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          fetchDashboardData();
        }}
      />

      <PartnerForm
        isOpen={isPartnerModalOpen}
        onClose={() => {
          setIsPartnerModalOpen(false);
          fetchDashboardData();
        }}
      />

      {tournamentId && (
        <EventModal
          isOpen={isEventModalOpen}
          onClose={() => {
            setIsEventModalOpen(false);
            fetchDashboardData();
          }}
          tournamentId={tournamentId}
          event={null}
        />
      )}
    </div>
  );
}

export default ManagerDashboardHome;
